import type { IGameLevel, StatDefinition } from './IGameLevel';
import type { IBeingState } from '../controllers/states/being/IBeingState';
import type { IBeastState } from '../controllers/states/beast/IBeastState';
import { FleeingState } from '../controllers/states/being/FleeingState';
import { ExploringState } from '../controllers/states/being/ExploringState';
import { EatingState } from '../controllers/states/being/EatingState';
import { RestingState } from '../controllers/states/being/RestingState';
import { DeadState } from '../controllers/states/being/DeadState';
import { SocializingState } from '../controllers/states/being/SocializingState';
import { HuntingState } from '../controllers/states/being/HuntingState';
import { BeastSleepingState } from '../controllers/states/beast/BeastSleepingState';
import { BeastEatingState } from '../controllers/states/beast/BeastEatingState';
import { BeastExploringState } from '../controllers/states/beast/BeastExploringState';
import { BeastFleeingState } from '../controllers/states/beast/BeastFleeingState';
import { BeastGoringState } from '../controllers/states/beast/BeastGoringState';
import { BeastController } from '../controllers/BeastController';
import { ToolType } from '../models/WorldModel';
import type { Being } from '../models/Being';
import type { Beast } from '../models/Beast';
import type { PubSub } from '../PubSub';
import type { GameEvents } from '../events';
import type { ConsoleController } from '../controllers/ConsoleController';
import type { WorldController } from '../controllers/WorldController';
import { LEVEL2, SOCIAL } from '../models/constants';
import { level2Intro, level2Complete } from '../assets/text';

export class Level2 implements IGameLevel {
    readonly id = 'level-2';

    /** `SpawnBeast` is present but gated by `isToolEnabled` — see below. */
    readonly tools: ToolType[] = [
        ToolType.Raise,
        ToolType.Lower,
        ToolType.Flatten,
        ToolType.SpawnBeast,
    ];

    /** Being state handlers. FleeingState listed first so danger always wins interrupt
     *  priority. Built in the constructor because SocializingState needs the event bus. */
    readonly beingStates: Map<string, IBeingState>;

    /** Beast state handlers. Interrupt priority follows insertion order:
     *  BeastGoringState first so a cornered beast turns on beings that get too close,
     *  then BeastFleeingState (lethal terrain and nearby hunting beings). Built in the
     *  constructor because BeastGoringState needs the event bus. */
    readonly beastStates: Map<string, IBeastState>;

    readonly stats: StatDefinition[] = [
        {
            label: 'BEAST KILLS',
            getValue: () => `${this.killCount}/${LEVEL2.BEASTS_TO_KILL}`,
            getFraction: () => Math.min(1, this.killCount / LEVEL2.BEASTS_TO_KILL),
        },
    ];

    private killCount = 0;
    /** Live beasts, refreshed every tick. Seeded with the starting population so the
     *  Spawn Beast tool reads as disabled before the first tick runs. */
    private liveBeastCount = LEVEL2.BEAST_COUNT;
    private isComplete = false;
    private consoleController: ConsoleController;
    private pubsub: PubSub<GameEvents>;
    private worldController: WorldController;

    constructor({ consoleController, pubsub, worldController }: {
        consoleController: ConsoleController;
        pubsub: PubSub<GameEvents>;
        worldController: WorldController;
    }) {
        this.consoleController = consoleController;
        this.pubsub = pubsub;
        this.worldController = worldController;

        this.beingStates = new Map([
            [DeadState.stateName,        new DeadState()],
            [FleeingState.stateName,     new FleeingState()],
            [HuntingState.stateName,     new HuntingState()],
            [SocializingState.stateName, new SocializingState({ pubsub })],
            [ExploringState.stateName,   new ExploringState({ socialWeight: SOCIAL.ATTRACTION_WEIGHT })],
            [EatingState.stateName,      new EatingState()],
            [RestingState.stateName,     new RestingState()],
        ]);

        this.beastStates = new Map([
            [BeastGoringState.stateName,    new BeastGoringState({ pubsub })],
            [BeastFleeingState.stateName,   new BeastFleeingState()],
            [BeastSleepingState.stateName,  new BeastSleepingState()],
            [BeastEatingState.stateName,    new BeastEatingState()],
            [BeastExploringState.stateName, new BeastExploringState()],
        ]);

        worldController.setBeastController(new BeastController({ states: this.beastStates }));

        pubsub.subscribe('world:beast-killed', () => {
            this.killCount++;
        });
    }

    /**
     * Gates the Spawn Beast tool on the population being wiped out. The level hands
     * the player a fixed herd to hunt, so manufacturing prey on demand would defeat
     * the point — but a run where every beast drowns or starves would otherwise be
     * unwinnable, so the tool unlocks as a recovery valve at zero and locks again as
     * soon as one exists.
     */
    isToolEnabled(tool: ToolType): boolean {
        if (tool !== ToolType.SpawnBeast) return true;
        return this.liveBeastCount === 0;
    }

    /**
     * Scatters the level's beasts across the map and plays the intro. The player has
     * no beast-spawning tool: the herd is presented with a fixed population to hunt,
     * so the challenge is shaping terrain to corner it rather than manufacturing prey.
     */
    begin(): void {
        this.worldController.spawnBeasts(LEVEL2.BEAST_COUNT);
        this.consoleController.play(level2Intro);
    }

    tick(_beings: Being[], beasts: Beast[]): void {
        this.liveBeastCount = beasts.length;

        if (!this.isComplete && this.killCount >= LEVEL2.BEASTS_TO_KILL) {
            this.isComplete = true;
            this.consoleController.play(level2Complete);
            this.pubsub.publish('level:complete', this.id);
        }
    }
}
